import { useId, useState } from 'react';
import { ErrorState } from './ErrorState';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EXPORTLEISTE (TX-STANDARD S1.9, Paket TX1)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Knöpfe PDF, Excel und Unterlagen (ZIP) einer Seite - immer in dieser
 * Reihenfolge, immer mit denselben Zuständen. Sie gehört in
 * `PageHeader.aktionen`. CSV bleibt Sache der einzelnen `DataTable`.
 *
 * Bis TX1 baute jede Seite ihren Knopf selbst (`AussenBerichtKnopf`,
 * `msr/PdfKnopf`, `AussparungsplanKnopf`, `AnforderungslisteKnopf`, …): mit
 * verschiedenen Texten, mit und ohne Fehleranzeige, mit Hinweisen nur im
 * Tooltip.
 *
 * Was die Leiste festlegt:
 *   - Während des Erstellens „wird erstellt …“ und gesperrt - ein zweiter
 *     Klick würde eine zweite Fassung mit eigener Nummer ziehen.
 *   - Ein Fehler steht als `ErrorState klein` unter der Leiste, mit
 *     „Erneut versuchen“.
 *   - „Fertig“ meldet sie über `aria-live` an Vorlesehilfen (S1.10, 4.1.3).
 *   - Hinweise stehen als TEXT unter dem Knopf und hängen über
 *     `aria-describedby` am Knopf - nie nur im Tooltip (S1.10).
 *   - Den Dateinamen setzt der Server (`Content-Disposition`); `laden` ruft
 *     dafür `api.dateiLaden(pfad, rueckfall)`.
 */

export type Exportart = 'pdf' | 'excel' | 'zip';

export interface ExportZiel {
  art: Exportart;
  /** Lädt die Datei herunter. Wirft bei einem Fehler. */
  laden: () => Promise<void>;
  /** Abweichende Beschriftung; Vorgabe „PDF“, „Excel“, „Unterlagen (ZIP)“. */
  beschriftung?: string;
  /**
   * PDF: Vergibt jeder Abruf eine neue Dokumentnummer? Vorgabe `false` seit TX2
   * (F11): Bei gleichem Stand liefert der Server die abgelegte Fassung. `true`
   * nur für Berichte, die weiter je Abruf neu entstehen (Zeichner mit Einfang,
   * Nachbearbeitung; TX-STANDARD S3.6, Befund §9.3) - dann steht der Hinweis
   * {@link HINWEIS_NEUE_FASSUNG}.
   */
  neueFassungJeAbruf?: boolean;
  /** Ein weiterer Hinweis als Text unter dem Knopf, z. B. „Sichtstand ohne Dokumentnummer“. */
  hinweis?: string;
  /** Gesperrt, mit Grund (Pflicht, sonst wirkt der Knopf kaputt). */
  gesperrt?: { grund: string };
}

export interface ExportLeisteProps {
  ziele: ExportZiel[];
  className?: string;
}

/** Der Hinweis an PDF-Knöpfen, die weiter je Abruf eine neue Fassung ziehen (S1.9, S3.6). */
export const HINWEIS_NEUE_FASSUNG =
  'Jeder Abruf erzeugt eine neue Fassung mit eigener Nummer.';

const REIHENFOLGE: Record<Exportart, number> = { pdf: 0, excel: 1, zip: 2 };

const VORGABE: Record<Exportart, string> = {
  pdf: 'PDF',
  excel: 'Excel',
  zip: 'Unterlagen (ZIP)',
};

/** Die Ziele in der festen Reihenfolge PDF, Excel, ZIP; Gleiches bleibt, wie es kam. */
export function ordneZiele(ziele: ExportZiel[]): ExportZiel[] {
  return ziele
    .map((z, i) => ({ z, i }))
    .sort((a, b) => REIHENFOLGE[a.z.art] - REIHENFOLGE[b.z.art] || a.i - b.i)
    .map((x) => x.z);
}

function beschriftungVon(z: ExportZiel): string {
  return z.beschriftung ?? VORGABE[z.art];
}

function hinweiseVon(z: ExportZiel): string[] {
  const h: string[] = [];
  if (z.art === 'pdf' && (z.neueFassungJeAbruf ?? false)) h.push(HINWEIS_NEUE_FASSUNG);
  if (z.hinweis) h.push(z.hinweis);
  if (z.gesperrt) h.push(z.gesperrt.grund);
  return h;
}

export function ExportLeiste({ ziele, className }: ExportLeisteProps) {
  const basis = useId();
  const [laeuft, setLaeuft] = useState<number | null>(null);
  const [fehler, setFehler] = useState<{ index: number; text: string } | null>(null);
  const [meldung, setMeldung] = useState('');

  const geordnet = ordneZiele(ziele);

  const hole = async (index: number) => {
    const z = geordnet[index];
    setLaeuft(index);
    setFehler(null);
    setMeldung(`${beschriftungVon(z)} wird erstellt.`);
    try {
      await z.laden();
      setMeldung(`${beschriftungVon(z)} erstellt.`);
    } catch (e) {
      const text = e instanceof Error ? e.message : String(e);
      setFehler({ index, text });
      setMeldung(`${beschriftungVon(z)} konnte nicht erstellt werden.`);
    } finally {
      setLaeuft(null);
    }
  };

  const klassen = ['m-exportleiste'];
  if (className) klassen.push(className);

  return (
    <div className={klassen.join(' ')}>
      <div className="m-exportleiste-knoepfe" role="group" aria-label="Ausgaben">
        {geordnet.map((z, i) => {
          const hinweise = hinweiseVon(z);
          const hinweisId = hinweise.length > 0 ? `${basis}-h${i}` : undefined;
          return (
            <div key={`${z.art}-${i}`} className="m-exportleiste-ziel" data-art={z.art}>
              <button
                type="button"
                className="m-knopf"
                disabled={laeuft !== null || z.gesperrt !== undefined}
                aria-describedby={hinweisId}
                aria-busy={laeuft === i}
                onClick={() => { void hole(i); }}
              >
                {laeuft === i ? 'wird erstellt …' : beschriftungVon(z)}
              </button>
              {hinweisId && (
                <span id={hinweisId} className="m-exportleiste-hinweis">
                  {hinweise.join(' ')}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {fehler && (
        <ErrorState
          klein
          titel={`${beschriftungVon(geordnet[fehler.index])} konnte nicht erstellt werden.`}
          meldung={fehler.text}
          beiErneutVersuchen={() => { void hole(fehler.index); }}
        />
      )}

      <span className="m-nur-vorlesen" aria-live="polite" role="status">{meldung}</span>
    </div>
  );
}
